import { useSession } from '@zhiyue/core';
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { clearOpenPlatformToken } from '@/services/openPlatformToken';
import {
  clearMemberDirectoryStore,
  getMemberDirectorySnapshot,
  type MemberDirectorySnapshot,
  memberDirectoryStore,
} from './store';

type MemberDirectoryProviderProps = {
  children: ReactNode;
};

const MemberDirectoryContext = createContext<MemberDirectorySnapshot | null>(
  null,
);

const getSnapshot = (): MemberDirectorySnapshot => getMemberDirectorySnapshot();

export const MemberDirectoryProvider = ({
  children,
}: MemberDirectoryProviderProps) => {
  const { currentUser, loading } = useSession();
  const identityKey = [
    currentUser?.tenantUuid,
    currentUser?.lastLoginOrganizationUuid,
    currentUser?.memberUuid,
  ]
    .map((value) => String(value || '').trim())
    .join('|');
  const previousIdentityKey = React.useRef('');

  useEffect(() => {
    if (loading || !currentUser) {
      clearOpenPlatformToken();
      clearMemberDirectoryStore();
      previousIdentityKey.current = '';
      return;
    }

    if (
      previousIdentityKey.current &&
      previousIdentityKey.current !== identityKey
    ) {
      clearOpenPlatformToken();
      clearMemberDirectoryStore();
    }
    previousIdentityKey.current = identityKey;

    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    if (pathname === '/user/login' || pathname === '/callback') {
      return;
    }

    void memberDirectoryStore.ensureMembersLoaded().catch(() => undefined);
  }, [currentUser, identityKey, loading]);

  const snapshot = useSyncExternalStore(
    memberDirectoryStore.subscribe,
    getSnapshot,
    getSnapshot,
  );

  const value = useMemo(() => snapshot, [snapshot]);

  return (
    <MemberDirectoryContext.Provider value={value}>
      {children}
    </MemberDirectoryContext.Provider>
  );
};

export const useMemberDirectory = () => {
  const context = useContext(MemberDirectoryContext);
  if (context) {
    return context;
  }
  return getMemberDirectorySnapshot();
};

export * from './member-label';
export * from './store';
